import React, { Component } from 'react';
import { bool, string, object } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Field, Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { isUploadImageOverLimitError } from '../../util/errors';
import {
    Form,
    PrimaryButton,
    ImageFromFile,
    IconSpinner,
    FieldTextInput,
    FieldSelect,
} from '../../components';
import config from '../../config.js';

import css from './ProfileSettingsForm.css';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcSet

class ProfileSettingsFormComponent extends Component {
    constructor(props) {
        super(props);

        this.uploadDelayTimeoutId = null;
        this.state = { uploadDelay: false };
        this.submittedValues = {};
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.uploadInProgress && !nextProps.uploadInProgress) {
            this.setState({ uploadDelay: true });
            this.uploadDelayTimeoutId = window.setTimeout(() => {
                this.setState({ uploadDelay: false });
            }, UPLOAD_CHANGE_DELAY);
        }
    }

    componentWillUnmount() {
        window.clearTimeout(this.blurTimeoutId);
    }

    render() {
        return (
            <FinalForm
                {...this.props}
                render={fieldRenderProps => {
                    const {
                        className,
                        currentUser,
                        handleSubmit,
                        intl,
                        invalid,
                        onImageUpload,
                        pristine,
                        profileImage,
                        rootClassName,
                        updateInProgress,
                        updateProfileError,
                        uploadImageError,
                        uploadInProgress,
                        form,
                        values,
                    } = fieldRenderProps;

                    const user = ensureCurrentUser(currentUser);

                    const userType = user.attributes.profile.userType; // user.attributes.profile.privateData.userType || ''

                    // First name
                    const firstNameLabel = intl.formatMessage({
                        id: 'ProfileSettingsForm.firstNameLabel',
                    });
                    const firstNamePlaceholder = intl.formatMessage({
                        id: 'ProfileSettingsForm.firstNamePlaceholder',
                    });
                    const firstNameRequiredMessage = intl.formatMessage({
                        id: 'ProfileSettingsForm.firstNameRequired',
                    });
                    const firstNameRequired = validators.required(firstNameRequiredMessage);

                    // Last name
                    const lastNameLabel = intl.formatMessage({
                        id: 'ProfileSettingsForm.lastNameLabel',
                    });
                    const lastNamePlaceholder = intl.formatMessage({
                        id: 'ProfileSettingsForm.lastNamePlaceholder',
                    });
                    const lastNameRequiredMessage = intl.formatMessage({
                        id: 'ProfileSettingsForm.lastNameRequired',
                    });
                    const lastNameRequired = validators.required(lastNameRequiredMessage);

                    // Organization Name
                    const OganizationNameLabel = intl.formatMessage({
                        id: 'ProfileSettingsForm.OganizationNameLabel',
                    });
                    const OganizationNamePlaceholder = intl.formatMessage({
                        id: 'ProfileSettingsForm.OganizationNamePlaceholder',
                    });
                    const OganizationNameRequiredMessage = intl.formatMessage({
                        id: 'ProfileSettingsForm.OganizationNameRequired',
                    });
                    const OganizationNameRequired = validators.required(
                        OganizationNameRequiredMessage
                    );

                    // Our work with Regenera
                    const OurWorkWithRegeneraLabel = intl.formatMessage({
                        id: 'ProfileSettingsForm.OurWorkWithRegeneraLabel',
                    });
                    const OurWorkWithRegeneraPlaceholder = intl.formatMessage({
                        id: 'ProfileSettingsForm.OurWorkWithRegeneraPlaceholder',
                    });

                    // Our Passion
                    const ourPassionLabel = intl.formatMessage({
                        id: 'ProfileSettingsForm.ourPassionLabel',
                    });
                    const ourPassionPlaceholder = intl.formatMessage({
                        id: 'ProfileSettingsForm.ourPassionPlaceholder',
                    });

                    const uploadingOverlay =
                        uploadInProgress || this.state.uploadDelay ? (
                            <div className={css.uploadingImageOverlay}>
                                <IconSpinner />
                            </div>
                        ) : null;

                    const hasUploadError = !!uploadImageError && !uploadInProgress;
                    const errorClasses = classNames({ [css.avatarUploadError]: hasUploadError });
                    const transientUserProfileImage =
                        profileImage.uploadedImage || user.profileImage;
                    const transientUser = { ...user, profileImage: transientUserProfileImage };

                    // Ensure that file exists if imageFromFile is used
                    const fileExists = !!profileImage.file;
                    const fileUploadInProgress = uploadInProgress && fileExists;
                    const delayAfterUpload = profileImage.imageId && this.state.uploadDelay;
                    const imageFromFile =
                        fileExists && (fileUploadInProgress || delayAfterUpload) ? (
                            <ImageFromFile
                                id={profileImage.id}
                                className={errorClasses}
                                rootClassName={css.uploadingImage}
                                aspectRatioClassName={css.squareAspectRatio}
                                file={profileImage.file}
                            >
                                {uploadingOverlay}
                            </ImageFromFile>
                        ) : null;

                    const avatarClasses = classNames(errorClasses, css.avatar, {
                        [css.avatarInvisible]: this.state.uploadDelay,
                    });
                    const avatarComponent =
                        !fileUploadInProgress && profileImage.imageId ? null : null;

                    const chooseAvatarLabel =
                        profileImage.imageId || fileUploadInProgress ? (
                            <div className={css.avatarContainer}>
                                {imageFromFile}
                                {avatarComponent}
                                <div className={css.changeAvatar}>
                                    <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                                </div>
                            </div>
                        ) : (
                            <div className={css.avatarPlaceholder}>
                                <div className={css.avatarPlaceholderText}>
                                    <FormattedMessage id="ProfileSettingsForm.addYourProfilePicture" />
                                </div>
                                <div className={css.avatarPlaceholderTextMobile}>
                                    <FormattedMessage id="ProfileSettingsForm.addYourProfilePictureMobile" />
                                </div>
                            </div>
                        );

                    const submitError = updateProfileError ? (
                        <div className={css.error}>
                            <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
                        </div>
                    ) : null;

                    const classes = classNames(rootClassName || css.root, className);
                    const submitInProgress = updateInProgress;
                    const submittedOnce = Object.keys(this.submittedValues).length > 0;
                    const pristineSinceLastSubmit =
                        submittedOnce && isEqual(values, this.submittedValues);
                    const submitDisabled =
                        invalid ||
                        pristine ||
                        pristineSinceLastSubmit ||
                        uploadInProgress ||
                        submitInProgress;

                    return (
                        <Form
                            className={classes}
                            onSubmit={e => {
                                this.submittedValues = values;
                                handleSubmit(e);
                            }}
                        >
                            <div className={css.sectionContainer}>
                                <h3 className={css.sectionTitle}>
                                    <FormattedMessage id="ProfileSettingsForm.yourProfilePicture" />
                                </h3>
                                <Field
                                    accept={ACCEPT_IMAGES}
                                    id="profileImage"
                                    name="profileImage"
                                    label={chooseAvatarLabel}
                                    type="file"
                                    form={null}
                                    uploadImageError={uploadImageError}
                                    disabled={uploadInProgress}
                                >
                                    {fieldProps => {
                                        const {
                                            accept,
                                            id,
                                            input,
                                            label,
                                            type,
                                            disabled,
                                            uploadImageError,
                                        } = fieldProps;
                                        const { name } = input;
                                        const onChange = e => {
                                            const file = e.target.files[0];
                                            form.change(`profileImage`, file);
                                            form.blur(`profileImage`);
                                            if (file != null) {
                                                const tempId = `${file.name}_${Date.now()}`;
                                                onImageUpload({ id: tempId, file });
                                            }
                                        };

                                        let error = null;

                                        if (isUploadImageOverLimitError(uploadImageError)) {
                                            error = (
                                                <div className={css.error}>
                                                    <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
                                                </div>
                                            );
                                        } else if (uploadImageError) {
                                            error = (
                                                <div className={css.error}>
                                                    <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className={css.uploadAvatarWrapper}>
                                                <label className={css.label} htmlFor={id}>
                                                    {label}
                                                </label>
                                                <input
                                                    accept={accept}
                                                    id={id}
                                                    name={name}
                                                    className={css.uploadAvatarInput}
                                                    disabled={disabled}
                                                    onChange={onChange}
                                                    type={type}
                                                />
                                                {error}
                                            </div>
                                        );
                                    }}
                                </Field>
                                <div className={css.tip}>
                                    <FormattedMessage id="ProfileSettingsForm.tip" />
                                </div>
                                <div className={css.fileInfo}>
                                    <FormattedMessage id="ProfileSettingsForm.fileInfo" />
                                </div>
                            </div>
                            <div className={css.sectionContainer}>
                                <h3 className={css.sectionTitle}>
                                    <FormattedMessage id="ProfileSettingsForm.yourName" />
                                </h3>
                                <div className={css.nameContainer}>
                                    <FieldTextInput
                                        className={css.firstName}
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        label={firstNameLabel}
                                        placeholder={firstNamePlaceholder}
                                        validate={firstNameRequired}
                                    />
                                    <FieldTextInput
                                        className={css.lastName}
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        label={lastNameLabel}
                                        placeholder={lastNamePlaceholder}
                                        validate={lastNameRequired}
                                    />
                                </div>
                            </div>
                            {console.log(user)}
                            {userType === 'guardian' || userType === 'empresa' ? (
                                <div className={classNames(css.sectionContainer, css.lastSection)}>
                                    <h3 className={css.sectionTitle}>
                                        <FormattedMessage id="ProfileSettingsForm.yourOrganizationInfo" />
                                    </h3>

                                    <div className={css.nameContainer}>
                                        <FieldTextInput
                                            type="text"
                                            id="organizationName"
                                            name="organizationName"
                                            label={OganizationNameLabel}
                                            placeholder={OganizationNamePlaceholder}
                                            validate={OganizationNameRequired}
                                        />
                                    </div>
                                    <div className={css.areaContainer}>
                                        <FieldTextInput
                                            type="textarea"
                                            id="ourWorkWithRegenera"
                                            name="ourWorkWithRegenera"
                                            label={OurWorkWithRegeneraLabel}
                                            placeholder={OurWorkWithRegeneraPlaceholder}
                                        />
                                    </div>
                                    <div className={css.areaContainer}>
                                        <FieldTextInput
                                            type="textarea"
                                            id="ourPassion"
                                            name="ourPassion"
                                            label={ourPassionLabel}
                                            placeholder={ourPassionPlaceholder}
                                        />
                                    </div>

                                    <div className={css.nameContainer}>
                                        <FieldSelect
                                            id="staticProfileImages"
                                            name="staticProfileImages"
                                            label="Selecciona las imágenes del paisaje"
                                        >
                                            {config.custom.staticProfilePhotos.map(item => (
                                                <option key={item.key} value={item.key}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        </FieldSelect>
                                    </div>
                                </div>
                            ) : null}
                            {/*
                  <div className={classNames(css.sectionContainer, css.lastSection)}>
                    <h3 className={css.sectionTitle}>
                      <FormattedMessage id="ProfileSettingsForm.bioHeading" />
                    </h3>
                    <FieldTextInput
                      type="textarea"
                      id="bio"
                      name="bio"
                      label={bioLabel}
                      placeholder={bioPlaceholder}
                    />
                    <p className={css.bioInfo}>
                      <FormattedMessage id="ProfileSettingsForm.bioInfo" />
                    </p>
                  </div>
                */}

                            {submitError}
                            <PrimaryButton
                                className={css.submitButton}
                                type="submit"
                                inProgress={submitInProgress}
                                disabled={submitDisabled}
                                ready={pristineSinceLastSubmit}
                            >
                                <FormattedMessage id="ProfileSettingsForm.saveChanges" />
                            </PrimaryButton>
                        </Form>
                    );
                }}
            />
        );
    }
}

ProfileSettingsFormComponent.defaultProps = {
    rootClassName: null,
    className: null,
    uploadImageError: null,
    updateProfileError: null,
    updateProfileReady: false,
};

ProfileSettingsFormComponent.propTypes = {
    rootClassName: string,
    className: string,

    uploadImageError: propTypes.error,
    uploadInProgress: bool.isRequired,
    updateInProgress: bool.isRequired,
    updateProfileError: propTypes.error,
    updateProfileReady: bool,

    // from injectIntl
    intl: object.isRequired,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
